<template>
  <AppLayout :title="$t('SONNENGLAS Produktkatalog')">
    <div class="min-h-screen bg-white">
      <!-- Main Content -->
      <div class="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <!-- Products Display by Category -->
        <div
          :class="categoryGroup.isParent && (!categoryGroup.products || categoryGroup.products.length === 0) ? 'mb-3' : 'mb-16'"
          v-for="categoryGroup in sortedProductsByGroup"
          :key="categoryGroup.categoryId"
          :id="categoryAnchorId(categoryGroup.categoryName, categoryGroup.categoryId)"
          class="scroll-mt-24"
        >
          <!-- Main Category Header (larger font, more bold, minimal bottom margin) -->
          <h2
            v-if="categoryGroup.isParent"
            class="text-2xl font-extrabold text-gray-900 mb-1.5 text-left"
          >
            {{ categoryGroup.categoryName }}
          </h2>

          <!-- Subcategory Header (smaller font, indented) -->
          <h3
            v-else
            class="text-lg font-bold text-gray-900 mb-6 text-left"
          >
            {{ categoryGroup.categoryName }}
          </h3>

          <!-- Products Grid (only if there are products) -->
          <div
            v-if="categoryGroup.products && categoryGroup.products.length > 0"
            class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            <ProductCard
              v-for="product in categoryGroup.products"
              :key="product.sku"
              :product="product"
              :tier-price="getTier3Price(product.sku)"
              :currency="currency"
              @add-to-cart="handleAddToCart"
            />
          </div>
        </div>

        <!-- Empty State -->
        <div v-if="!products || products.length === 0" class="text-center py-12">
          <div class="text-gray-500">
            <h3 class="text-lg font-medium">{{ $t('Keine Produkte verfügbar') }}</h3>
            <p class="mt-2">{{ $t('Bitte versuchen Sie es später erneut.') }}</p>
          </div>
        </div>
      </div>

      <!-- Cart Sidebar -->
      <CartSidebar />
      
      <!-- Add to Cart Modal -->
      <AddToCartModal 
        :is-open="showModal"
        :product-name="modalProductName"
        :quantity="modalQuantity"
        @close="closeModal"
      />
    </div>
  </AppLayout>
</template>

<script setup lang="ts">
import { onMounted, computed, ref, nextTick } from 'vue'
import { router } from '@inertiajs/vue3'
import { useProductsStore } from '@/stores/products'
import { useCartStore } from '@/stores/cart'
import { useCartOperations } from '@/composables/useCartOperations'
import { usePricing } from '@/composables/usePricing'
import AppLayout from '@/layouts/AppLayout.vue'
import ProductCard from '@/components/ProductCard.vue'
import CartSidebar from '@/components/CartSidebar.vue'
import AddToCartModal from '@/components/AddToCartModal.vue'
import { categoryAnchorId } from '@/utils/slug'
import type { Product } from '@/types/product'

interface Category {
  id: number
  name: string
  parent_category_id: number | null
  sort_order: number
  product_count: number
  children?: Category[]
}

// Props passed from Inertia (ProductsController)
interface Props {
  products: Product[]
  categories: Category[]
  currency: string
  quantities: Record<string, number>
  pricingTiers: PartnerPortal.Xhosa.Contracts.DTOs.ResellerPricingTierDto[]
  productTierPricing: Record<string, Record<string, number>>
  hiddenProducts?: Array<{ sku: string; name: string; reason: string }>
}

const props = defineProps<Props>()

const productsStore = useProductsStore()
const cartStore = useCartStore()

const { addToCart } = cartStore

// DRY REFACTOR: Use shared composables
const { showModal, modalProductName, modalQuantity, closeModal, handleAddToCart } = useCartOperations()
const { getTier3Price } = usePricing(props.productTierPricing)

// Initialize stores with real data from props
onMounted(() => {
  productsStore.setProducts(props.products)

  // Initialize cart store with products, cart quantities, pricing tiers, and tier pricing
  const transformedTiers = props.pricingTiers.map(tier => ({
    id: tier.id,
    name: tier.name,
    discount: 0, // Not used anymore with real tier pricing
    revenueThreshold: tier.revenueThreshold,
    currency: tier.currency
  }))

  cartStore.initializeCart(props.products, props.quantities, transformedTiers, props.productTierPricing, props.currency)

  // Inertia visits do not honour URL hashes by default. After category groups
  // render, scroll to the requested anchor (e.g. /products#category-ersatzteile).
  scrollToHashCategory()
})

const scrollToHashCategory = (): void => {
  const hash = window.location.hash
  if (!hash || hash.length < 2) {
    return
  }

  const targetId = decodeURIComponent(hash.substring(1))

  // Wait for DOM updates triggered by onMounted before resolving the target.
  nextTick(() => {
    const el = document.getElementById(targetId)
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  })
}

// Get products organized by category in proper order
const sortedProductsByGroup = computed(() => {
  // First group products by category
  const grouped: Record<number, Product[]> = {}

  props.products.forEach(product => {
    const categoryId = product.categoryId

    if (!grouped[categoryId]) {
      grouped[categoryId] = []
    }
    grouped[categoryId].push(product)
  })

  // Convert to sorted array based on category order from API
  const sortedGroups = []

  // Get current locale for category names
  const currentLocale = (document.documentElement.lang || 'de').toLowerCase()

  // Helper to get localized category name
  const getLocalizedName = (category: any) => {
    return currentLocale === 'en'
      ? (category.nameEn || category.name_en || category.name)
      : (category.nameDe || category.name_de || category.name)
  }

  // Helper to check if a category has any products (including in subcategories)
  const hasProductsInCategoryTree = (category: any): boolean => {
    // Check if this category has products
    if (grouped[category.id] && grouped[category.id].length > 0) {
      return true
    }

    // Check if any children have products
    if (category.children && category.children.length > 0) {
      return category.children.some((child: any) =>
        grouped[child.id] && grouped[child.id].length > 0
      )
    }

    return false
  }

  // If categories are provided by backend, use them
  if (props.categories && props.categories.length > 0) {
    // Sort categories by sort_order to ensure correct display order
    const sortedCategories = [...props.categories].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

    for (const category of sortedCategories) {
      const categoryId = category.id
      const hasChildren = category.children && category.children.length > 0

      // Only show categories that have products (either directly or in subcategories)
      if (!hasProductsInCategoryTree(category)) {
        continue
      }

      // Always add main category header if it has products or if its children have products
      sortedGroups.push({
        categoryId: categoryId,
        categoryName: getLocalizedName(category),
        products: grouped[categoryId] || [], // Empty array if no products in this category
        isParent: !category.parent_category_id // Mark as parent if it has no parent
      })

      // Add subcategories if they exist and have products
      if (hasChildren) {
        // Sort children by sort_order as well
        const sortedChildren = [...category.children].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))

        for (const child of sortedChildren) {
          const childId = child.id
          // Only add subcategories that have products
          if (grouped[childId] && grouped[childId].length > 0) {
            sortedGroups.push({
              categoryId: childId,
              categoryName: getLocalizedName(child),
              products: grouped[childId],
              isParent: false // Subcategories are never parents
            })
          }
        }
      }
    }
  } else {
    // Fallback: Create groups from product categoryIds when backend categories are empty
    // Sort by categoryId to maintain some order
    const categoryIds = Object.keys(grouped).map(Number).sort((a, b) => a - b)

    for (const categoryId of categoryIds) {
      sortedGroups.push({
        categoryId: categoryId,
        categoryName: `Category ${categoryId}`,
        products: grouped[categoryId],
        isParent: false
      })
    }
  }

  return sortedGroups
})

// Get category name by ID (searching in hierarchical categories) with translation support
const getGroupName = (categoryId: number): string => {
  // Search in top-level categories
  for (const category of props.categories) {
    if (category.id === categoryId) {
      // Category name comes from API with localization via getLocalizedName()
      return category.name
    }
    // Search in children recursively
    if (category.children && category.children.length > 0) {
      for (const child of category.children) {
        if (child.id === categoryId) {
          return child.name
        }
      }
    }
  }
  return `Kategorie ${categoryId}`
}


// Access to products for empty state check
const products = computed(() => props.products)
</script>